import React, { useEffect, useState, useCallback } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import { useMediaQuery } from "react-responsive";
import Navbar from "./components/NavBar";
import Sidebar from "./components/Sidebar";
import HandleRedirectError from "./components/HandleRedirectError";
import ProtectedRoute from "./components/ProtectedRoute";

// Pages
// import Home from "./components/home/Home";
import Dashboard from "./components/Dashboard";
import RegisterUser from "./components/Registration/RegisterUser";
import BusinessMembershipForm from "./components/Registration/BusinessMembershipForm";
import YouthMembershipForm from "./components/Registration/YouthMembershipForm";
import NotificationsPage from "./components/notifications/Notifications";
import Events from "./components/events/Events";
import EventAttendees from "./components/events/EventAttendees";
import AttendeeNotes from "./components/events/AttendeeNotes";
import ConfirmBooking from "./components/events/ConfirmBooking";
import ContactUs from "./components/contact-us/ContactUs";
import MyAccount from "./components/Account/MyAccount";
import Connections from "./components/Connections/Connections";
import Messages from "./components/Messages/Messages";
// import Investing from "./components/Investing";
import InternationalMarkets from "./components/IntenationalMarkets";
import MentorAcademy from "./components/MentorAcademy";
import VentureBuilder from "./components/VentureBuilder";
import Admin from "./components/Admin/Admin";
import Vision from "./components/vision/Vision";
import EventsPage from "./components/events/EventsPage";
import SupportUs from "./components/support/SupportUs";
import BusinessMembership from "./components/member-benefits/BusinessMembership";
import ExecutiveMembership from "./components/member-benefits/ExecutiveMembership";
import YouthMembership from "./components/member-benefits/YouthMembership";
// import InternationalTrade from "./components/international-trade/InternationalTrade";
import ExecutiveInMembership from "./components/international-trade/ExecutiveInMembership";
// import SaudiProgram from "./components/international-trade/SaudiProgram";
// import FranceBusiness from "./components/international-trade/FranceBusiness";
import AuthHandler from "./components/AuthHandler";
import ExplorerProfilePhotoRequiredModal from "./components/explorer/ExplorerProfilePhotoRequiredModal";
import EventDetails from "./components/Admin/EventDetails";
import Login from "./components/Login";
import Pitch from "./components/pitch/CreatePitch";
import SignalRMessageListener from "./components/Test/SignalRMessageListener";
import ContactConfirmation from "./components/contact-us/ContactConfirmation";
import EventQuestions from "./components/events/event-questions";
import UserProfile from "./components/User/UserProfile";
import VideoCall from "./components/Messages/VideoCall";
import Posts from "./components/Feed/Posts";
import Partners from "./components/Partners/Partners";
import RegisterProfile from "./components/MuslimInvest/RegisterProfile";
import MuslimInvestRoute from "./components/MuslimInvest/MuslimInvestRoute";
import InvestorProfile from "./components/MuslimInvest/InvestorProfile/InvestorProfile";
import CompanyProfile from "./components/MuslimInvest/CompanyProfile/CompanyProfile";
import InvestorCheckout from "./components/MuslimInvest/InvestorProfile/InvestorCheckout";
import MyPitch from "./components/pitch/MyPitch";
import ViewPitch from "./components/pitch/ViewPitch";
import ManageMembership from "./components/membership/ManageMembership";
import CancelMembership from "./components/membership/CancelMembership";
import RenewMembership from "./components/membership/RenewMembership";
import MembershipInactive from "./components/membership/MembershipInactive";
import { isLoggedIn } from "./auth/authStorage";
import { useMembershipSidebarGate } from "./hooks/useMembershipSidebarGate";
import ExplorerLegacyAuthRedirect from "./components/explorer/ExplorerLegacyAuthRedirect";
import ExplorerSegmentation from "./components/explorer/ExplorerSegmentation";
import ExplorerCheckout from "./components/explorer/ExplorerCheckout";
import { useTranslation } from "react-i18next";

const VIDEO_HUB_URL =
  "https://smbfglobal-g6bbhufugxchfncw.uksouth-01.azurewebsites.net/videoHub";
const LIVEKIT_TOKEN_ENDPOINT =
  "https://smbfglobal-g6bbhufugxchfncw.uksouth-01.azurewebsites.net/Video/token";

const DESKTOP_MIN_WIDTH = 1024;

/** Old URL: step 3 was removed from the explorer flow. */
function ExplorerDiscoveryRedirect() {
  return isLoggedIn() ? (
    <Navigate to="/explorer/checkout" replace />
  ) : (
    <Navigate to="/explorer/segmentation" replace />
  );
}

const Layout: React.FC = () => {
  const { i18n } = useTranslation();
  const uiLang = i18n.resolvedLanguage ?? i18n.language;
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const isAuthenticated = isLoggedIn();
  const isDesktop = useMediaQuery({ minWidth: DESKTOP_MIN_WIDTH });
  const {
    lockSidebarForMembership,
    hideSidebarOnRegistrationWizard,
  } = useMembershipSidebarGate();

  const location = useLocation();

  /** Full-screen for video and explorer wizard (segmentation → checkout). */
  const hideNavAndSidebar =
    location.pathname.startsWith("/video-call") ||
    location.pathname === "/explorer/segmentation" ||
    location.pathname === "/explorer/checkout";

  const hideSidebar = hideNavAndSidebar || hideSidebarOnRegistrationWizard;

  /** Top offset only when Navbar + sidebar chrome is actually rendered */
  const showAppChrome = isAuthenticated && !hideNavAndSidebar;

  useEffect(() => {
    if (hideSidebar) {
      setSidebarOpen(false);
      return;
    }
    // Desktop: always expanded. Mobile/tablet: start collapsed (icon rail).
    setSidebarOpen(window.innerWidth >= DESKTOP_MIN_WIDTH);
  }, [hideSidebar, location.pathname]);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [location.pathname]);

  const toggleSidebar = useCallback(() => {
    // Desktop stays permanently expanded — ignore toggle.
    if (window.innerWidth >= DESKTOP_MIN_WIDTH) return;
    setSidebarOpen((prev) => !prev);
  }, []);

  const closeSidebarOnMobile = useCallback(() => {
    if (window.innerWidth < DESKTOP_MIN_WIDTH) {
      setSidebarOpen(false);
    }
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (hideSidebar) {
        setSidebarOpen(false);
        return;
      }
      if (window.innerWidth >= DESKTOP_MIN_WIDTH) {
        setSidebarOpen(true);
      } else {
        setSidebarOpen(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [hideSidebar]);

  return (
    <>
      {isAuthenticated && !hideNavAndSidebar && (
        <>
          <Navbar
            key={`navbar-${uiLang}`}
          />
          <AuthHandler />
          <HandleRedirectError />
          <ExplorerProfilePhotoRequiredModal />
          {!hideSidebarOnRegistrationWizard && (
            <Sidebar
              key={`sidebar-${uiLang}`}
              isOpen={sidebarOpen}
              toggleSidebar={toggleSidebar}
              closeSidebarOnMobile={closeSidebarOnMobile}
              membershipLocked={lockSidebarForMembership}
            />
          )}
        </>
      )}
      <div
        className="content-wrapper"
        style={
          isAuthenticated && !hideSidebar
            ? {
                // Mobile/tablet: keep icon-rail offset only — open sidebar overlays body
                // Desktop: push body when expanded
                ...(i18n.dir() === "rtl"
                  ? {
                      marginRight: isDesktop ? 215 : 48,
                      marginLeft: 0,
                    }
                  : {
                      marginLeft: isDesktop ? 215 : 48,
                    }),
                transition: "margin-left 0.5s ease, margin-right 0.5s ease",
              }
            : undefined
        }
      >
        <main
          className={showAppChrome ? "px-2" : "mt-0"}
          style={
            showAppChrome
              ? {
                  // Match fixed navbar: 80px content + iOS safe-area (0 elsewhere)
                  paddingTop:
                    "calc(80px + env(safe-area-inset-top, 0px) + 0.5rem)",
                }
              : undefined
          }
        >
          <Routes>
            {/* Public Routes */}
            <Route
              path="/"
              element={
                isAuthenticated ? (
                  <div className="d-flex justify-content-center align-items-center vh-100">
                    <Spinner animation="border" role="status" variant="primary" />
                  </div>
                ) : (
                  <Login />
                )
              }
            />
            <Route path="/our-vision" element={<Vision />} />
            <Route path="/events" element={<EventsPage />} />
            <Route path="/support-our-growth" element={<SupportUs />} />
            <Route
              path="/business-membership"
              element={<BusinessMembership />}
            />
            <Route
              path="/executive-membership"
              element={<ExecutiveMembership />}
            />
            <Route path="/youth-membership" element={<YouthMembership />} />
            <Route path="/early-stage-membership" element={<YouthMembership />} />
            <Route path="/explorer/join" element={<ExplorerLegacyAuthRedirect />} />
            <Route path="/explorer/signup" element={<ExplorerLegacyAuthRedirect />} />
            <Route path="/explorer/signup/:eventCode" element={<ExplorerLegacyAuthRedirect />} />
            {/* <Route
              path="/international-business-set-up-programme"
              element={<InternationalTrade />}
            /> */}
            <Route
              path="/executive-international-membership"
              element={<ExecutiveInMembership />}
            />
            {/* <Route
              path="/saudi-arabia-international-trade-programme"
              element={<SaudiProgram />}
            />
            <Route
              path="/france-business-delegation-2025"
              element={<FranceBusiness />}
            /> */}

            <Route
              path="/explorer/segmentation"
              element={<ExplorerSegmentation />}
            />
            <Route
              path="/explorer/discovery"
              element={<ExplorerDiscoveryRedirect />}
            />
            <Route path="/explorer/checkout" element={<ExplorerCheckout />} />
            {/* Protected Routes */}
            <Route
              path="/updates"
              element={
                <ProtectedRoute>
                  <Posts />
                </ProtectedRoute>
              }
            />
            <Route
              path="/partners"
              element={
                <ProtectedRoute>
                  <Partners />
                </ProtectedRoute>
              }
            />
            <Route
              path="/event-questions/:eventId"
              element={
                <ProtectedRoute>
                  <EventQuestions />
                </ProtectedRoute>
              }
            />
            <Route
              path="/investing/company-profile"
              element={
                <ProtectedRoute>
                  <MuslimInvestRoute>
                    <CompanyProfile />
                  </MuslimInvestRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/investing/investor-profile/investor-checkout"
              element={
                <ProtectedRoute>
                  <MuslimInvestRoute>
                    <InvestorCheckout />
                  </MuslimInvestRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/investing/investor-profile"
              element={
                <ProtectedRoute>
                  <MuslimInvestRoute>
                    <InvestorProfile />
                  </MuslimInvestRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/investing"
              element={
                <ProtectedRoute>
                  <MuslimInvestRoute>
                    <RegisterProfile />
                  </MuslimInvestRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/investing/pitch"
              element={
                <ProtectedRoute>
                  <MuslimInvestRoute>
                    <Pitch />
                  </MuslimInvestRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/investing/my-pitches"
              element={
                <ProtectedRoute>
                  <MuslimInvestRoute>
                    <MyPitch />
                  </MuslimInvestRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/investing/pitch/:id"
              element={
                <ProtectedRoute>
                  <MuslimInvestRoute>
                    <ViewPitch />
                  </MuslimInvestRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path="/register-user"
              element={
                <ProtectedRoute>
                  <RegisterUser />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/notifications"
              element={
                <ProtectedRoute>
                  <NotificationsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/contact-confirmation"
              element={
                <ProtectedRoute>
                  <ContactConfirmation />
                </ProtectedRoute>
              }
            />
            <Route
              path="/business-membership-form"
              element={
                <ProtectedRoute>
                  <BusinessMembershipForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/youth-membership-form"
              element={
                <ProtectedRoute>
                  <YouthMembershipForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/event-booking"
              element={
                <ProtectedRoute>
                  <Events />
                </ProtectedRoute>
              }
            />
            <Route
              path="/event-booking/attendees/:eventId"
              element={
                <ProtectedRoute>
                  <EventAttendees />
                </ProtectedRoute>
              }
            />
            <Route
              path="/attendee-notes"
              element={
                <ProtectedRoute>
                  <AttendeeNotes />
                </ProtectedRoute>
              }
            />
            <Route
              path="/confirm-booking/:eventId"
              element={
                <ProtectedRoute>
                  <ConfirmBooking />
                </ProtectedRoute>
              }
            />
            <Route
              path="/contact-us"
              element={
                <ProtectedRoute>
                  <ContactUs />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-account"
              element={
                <ProtectedRoute>
                  <MyAccount />
                </ProtectedRoute>
              }
            />
            <Route
              path="/manage-membership"
              element={
                <ProtectedRoute>
                  <ManageMembership />
                </ProtectedRoute>
              }
            />
            <Route
              path="/cancel-membership"
              element={
                <ProtectedRoute>
                  <CancelMembership />
                </ProtectedRoute>
              }
            />
            <Route
              path="/renew-membership"
              element={
                <ProtectedRoute>
                  <RenewMembership />
                </ProtectedRoute>
              }
            />
            <Route
              path="/membership-inactive"
              element={
                <ProtectedRoute>
                  <MembershipInactive />
                </ProtectedRoute>
              }
            />
            <Route
              path="/connections"
              element={
                <ProtectedRoute>
                  <Connections />
                </ProtectedRoute>
              }
            />
            <Route
              path="/messages/:id?"
              element={
                <ProtectedRoute>
                  <Messages />
                </ProtectedRoute>
              }
            />
            <Route
              path="/user-profile/:id?"
              element={
                <ProtectedRoute>
                  <UserProfile />
                </ProtectedRoute>
              }
            />

            <Route
              path="/video-call/:targetObjectId?"
              element={
                <ProtectedRoute>
                  <VideoCall
                    signalRHubUrl={VIDEO_HUB_URL}
                    liveKitTokenEndpoint={LIVEKIT_TOKEN_ENDPOINT}
                  />
                </ProtectedRoute>
              }
            />

            {/* <Route
              path="/investing"
              element={
                <ProtectedRoute>
                  <Investing />
                </ProtectedRoute>
              }
            /> */}
            <Route
              path="/international-markets"
              element={
                <ProtectedRoute>
                  <InternationalMarkets />
                </ProtectedRoute>
              }
            />
            <Route
              path="/mentor-academy"
              element={
                <ProtectedRoute>
                  <MentorAcademy />
                </ProtectedRoute>
              }
            />
            <Route
              path="/venture-builder"
              element={
                <ProtectedRoute>
                  <VentureBuilder />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <Admin />
                </ProtectedRoute>
              }
            />
            <Route path="/admin/event/:id" element={<EventDetails />} />
            <Route
              path="/test"
              element={
                <ProtectedRoute>
                  <SignalRMessageListener />
                </ProtectedRoute>
              }
            />
            <Route
              path="*"
              element={
                <ProtectedRoute>
                  <Navigate to="/dashboard" replace />
                </ProtectedRoute>
              }
            />
          </Routes>
        </main>
        {/* {!isAuthenticated && <Footer />} */}
      </div>
    </>
  );
};

Layout.displayName = "Layout";

export default Layout;
